import { AfterViewInit, Component, OnDestroy, OnInit, HostListener, ViewEncapsulation } from '@angular/core';
import { GlobalKeyboardShortcutsService } from './common/keyboard-service/global-keyboard-shortcuts.service';
import { WorkItemNotificationsService } from './shared/services/workitem-notifications/workitem-notifications.service';
import { LoginSubscriber } from './shared/services/subscribers/login-subscriber.service';
import { LogoutSubscriber } from './shared/services/subscribers/logout-subscriber.service';
import { filter, Subject, Subscription, take } from 'rxjs';
import { ActivityHandlerService } from './common/inactivity-handler/activity-handler.service';
import { BcSettingsService } from './common/settings/settings.service';
import { NotificationBarService } from './shared/services/notification/notification-bar.service';
import { BcConfigService } from './common/config/provider';
import { StreamStatusService } from './shared/services/stream-status/stream-status.service';
import {
  WebConsoleContainerComponent
} from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/components/web-console-container';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { debounce } from 'lodash';
import { WindowSizeService } from 'common/window-size/window-size.service';
import { WindowService } from 'shared/services/window/window.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { VideoService } from 'shared/services/video-service/video.service';
import { CommonAuthService } from 'common/auth/common-auth.service';
import { Router } from '@angular/router';
import { GenesysUserPermissionService } from 'common/store/genesys-cloud/users/services/user-permission.service';
import { UserPermission } from 'common/store/genesys-cloud/users/models/user-permission.enum';
import { ModalService } from 'shared/components/modal-dialog/modal-dialog.service';

@Component({
  selector: 'app-live-chat',
  templateUrl: './live-chat.component.html',
  styleUrls: ['./live-chat.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LiveChatComponent implements OnInit, OnDestroy, AfterViewInit {
  public isVideoFeatureEnabled: boolean;
  private _hasVendorRemoteControl$ = new Subject<boolean>();
  private _loginSubscription: Subscription;
  private _logoutSubscription: Subscription;
  private _gcPermissionSubscription: Subscription;

  constructor(private bcNotificationService: NotificationBarService,
              private globalKeyboardShortcutsService: GlobalKeyboardShortcutsService,
              private workItemNotificationsService: WorkItemNotificationsService,
              private LoginSubscriber: LoginSubscriber,
              private LogoutSubscriber: LogoutSubscriber,
              private bcSettingsService: BcSettingsService,
              private activityService: ActivityHandlerService,
              private bcConfig: BcConfigService,
              private streamStatusService: StreamStatusService,
              private bcAnalytics: BcAnalytics,
              private videoService: VideoService,
              private _windowSizeService: WindowSizeService,
              private _windowService: WindowService,
              private _commonAuth: CommonAuthService,
              private _router: Router,
              private _gcPermissionService: GenesysUserPermissionService,
              private _bcModalService: ModalService
  ) {
    this.isVideoFeatureEnabled = false;
    this._commonAuth.isBoldEnabled().then(isBoldEnabled => {
      if (isBoldEnabled) {
        // Only init stream status notification when Bold backend is enabled
        this.streamStatusService.init();
      }
    });
  }

  public get isLoginSubscriptionActive(): boolean {
    return !!this._loginSubscription;
  }

  public get isLogoutSubscriptionActive(): boolean {
    return !!this._logoutSubscription;
  }

  async ngOnInit(): Promise<void> {
    this._loginSubscription = await this._commonAuth.isBoldEnabled() && this.LoginSubscriber.subscribe(async () => {
      await this.initSubscriptions();

      this._collectFeatureValues();
    });
    this._logoutSubscription = this.LogoutSubscriber.subscribe(() => {
      this._clearSubscriptionsOnLogout();
    });

    this._gcPermissionSubscription = this._gcPermissionService.hasAnyPermission$(
      UserPermission.ConversationCallAccept, UserPermission.ConversationCallAdd, UserPermission.ConversationCallRecord,
      UserPermission.ConversationCallbackAccept, UserPermission.ConversationCallbackCreate
    ).pipe(
      filter(hasAnyPermission => hasAnyPermission === true),
      take(1)
    ).subscribe(() => {
      const modalOptions = {
        headerText: 'modalDialog.attention',
        messageBody: 'modalDialog.voiceInteractionsWarning',
        messageNote: 'modalDialog.voiceInteractionsNote',
        icon: 'alert-warning-triangle',
        confirmButton: {
          text: 'bcOpStatus.ok'
        },
        cancelButton: {
          text: ''
        },
        closeable: true,
        doNotShowAgainById: 'conversationCallNotAvailable'
      };
      this._bcModalService.show(modalOptions);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this._debouncedResize();
    this._windowSizeService.onResize(this._windowService.innerWidth);
  }

  ngOnDestroy(): void {
    this.streamStatusService.destroy();
    this._clearSubscriptions();
  }

  ngAfterViewInit(): void {
    this.globalKeyboardShortcutsService.registerGlobalKeyboardShortcuts();

    this._commonAuth.isBoldEnabled().then(isBoldEnabled => {
      if (isBoldEnabled) {
        this._hasVendorRemoteControl$.subscribe(hasVendorRemoteControl => {
          if (!hasVendorRemoteControl) {
            // Pre-loading WebConsole script so it's already available when needed
            WebConsoleContainerComponent.loadExternalScript(this.bcConfig.current.webConsoleScriptUrl).catch(() => {
              this.bcAnalytics.eventTrack('WebConsoleScriptLoadError');
            });
          }
        });
      }
    });
  }

  hasNotifications(): boolean {
    return this.bcNotificationService.getNotifications().length > 0;
  }

  public async initSubscriptions(): Promise<void> {
    this.workItemNotificationsService.initNotificationSubscriptions();
    this.videoService.initSubscriptions();

    if (this._isAgentLogOutEnabled()) {
      const autoLogoutTimeoutInMinutes = await this.bcSettingsService.getInactivityTimeout();
      if (autoLogoutTimeoutInMinutes) {
        this.activityService.initialize(autoLogoutTimeoutInMinutes);
      }
    }
  }

  private _collectFeatureValues() {
    this.bcSettingsService.getSettings().then(settings => {
      this.isVideoFeatureEnabled = this.bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_VIDEO_CHAT)
        && this.bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_NEW_VIDEO_CHAT);
      this._hasVendorRemoteControl$.next(this.bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.RC_NEW_REMOTE_CONTROL));
    });
  }

  private _clearSubscriptions(): void {
    if (this._loginSubscription) {
      this._loginSubscription.unsubscribe();
      this._loginSubscription = null;
    }

    if (this._logoutSubscription) {
      this._logoutSubscription.unsubscribe();
      this._logoutSubscription = null;
    }

    if (this._gcPermissionSubscription) {
      this._gcPermissionSubscription.unsubscribe();
      this._gcPermissionSubscription = null;
    }

    this.videoService.clearSubscriptions();

    this._clearSubscriptionsOnLogout();
  }

  private _clearSubscriptionsOnLogout(): void {
    this.workItemNotificationsService.clearSubscriptions();
    if (this._isAgentLogOutEnabled()) {
      this.activityService.cleanUp();
    }
  }

  private _isAgentLogOutEnabled(): boolean {
    return this.bcConfig.current.features.agentAutoLogout;
  }

  private _debouncedResize = debounce(() => {
    const displayInfo = {
      screenDimensions: { width: screen.width, height: screen.height },
      windowDimension: { width: window.innerWidth, height: window.innerHeight }
    };
    this.bcAnalytics.updateUserProperties(displayInfo);
    this.bcAnalytics.eventTrack('WindowResize', displayInfo);
  }, 2000);
}
